import { PaletteCategory, TMargin, TWidgetCommonOption } from '@libs/types';

export const DEFAULT_STYLE = 'primary' as const;

export type TCoreOptions = {
    padding?: TMargin;
    alignment?: ALIGNMENT_ENUM;
    disabled?: PaletteCategory;
    hovered?: PaletteCategory;
    error?: PaletteCategory;
    pressed?: PaletteCategory;
} & TWidgetCommonOption &
    PaletteCategory;

export type TCoreButtonOptions = {
    borderRadius?: number;
    iconSrc?: string;
    iconSize?: number;
    fillContainer?: boolean;
    width?: number;
    maxWidth?: string;
    height?: number;
    maxHeight?: string;
    elevation?: number;
    boxShadow?: string;
    backgroundType?: 'plain' | 'gradient';
    labelPlacement?: 'start' | 'end';
    maxLines?: number;
    textAlign?: 'center' | 'start' | 'end';
    borderStroke?: number;
    cursorDisabled?: string;
    gap?: number;
    widthTransition?: string;
} & TCoreOptions;

export type TCoreInputOptions = {
    inputBorderRadius?: number;
    fillContainer?: boolean;
    isError?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    height?: number;
} & TCoreOptions;

export type TCoreTextOptions = {
    textAlign?: 'inherit' | 'left' | 'center' | 'right' | 'justify' | 'start' | 'end';
    textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
    maxLines?: number;
} & TCoreOptions;

export type TCoreCheckboxOptions = {
    size?: number;
    width?: number;
    height?: number;
    borderStroke?: number;
    borderRadius?: number;
    thumbSize?: number;
    thumbSizeChecked?: number;
    thumbOffset?: number;
    thumbOffsetChecked?: number;
} & TCoreOptions;

export type TCoreDividerOptions = {
    /** Line thickness in px (maps to the CSS border width). Defaults to 1 when unset. */
    thickness?: number;
} & TCoreOptions;

export type TCoreImageOptions = {
    width?: number | string;
    defaultIconWidth?: number | string;
    height?: number | string;
    defaultIconHeight?: number | string;
    borderRadius?: number;
    objectFit?: 'fill' | 'fit' | 'cover';
    overflow?: 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto';
    iconColor?: string;
    iconColorDisabled?: string;
    spinnerDelay?: number;
    defaultIconName?: string;
    disabled?: TCoreImageOptions;
} & TCoreOptions;

export enum ALIGNMENT_ENUM {
    CENTER = 'center',
    CENTER_LEFT = 'center_left',
    CENTER_RIGHT = 'center_right',
    TOP_LEFT = 'top_left',
    TOP_CENTER = 'top_center',
    TOP_RIGHT = 'top_right',
    BOTTOM_LEFT = 'bottom_left',
    BOTTOM_CENTER = 'bottom_center',
    BOTTOM_RIGHT = 'bottom_right',
}

export enum ORIENTATION_ENUM {
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal',
    BOX = 'box',
}
export type AlignmentMap = {
    top: 'flex-start';
    start: 'flex-start';
    center: 'center';
    bottom: 'flex-end';
    end: 'flex-end';
    space_between: 'space-between';
    space_around: 'space-around';
    space_evenly: 'space-evenly';
    stretch: 'stretch';
};
type Line = keyof AlignmentMap | undefined;

/**
 * This is common layout options for all layouts.
 *
 * @property backgroundImage - Absolute URL (http/https) of an image that fills the layout background.
 * @property backgroundColor - Background of the layout as a ready-to-use CSS value
 *                             (`var(--fill_primary_default)`, `#0b0b0b`, a gradient, …) — it feeds the
 *                             `background` shorthand, so gradients and `url()` values are accepted too.
 */
export type TCoreLayoutOptions = {
    orientation?: ORIENTATION_ENUM;
    lineX?: Line;
    lineY?: Line;
    backgroundColor?: string;
    backgroundImage?: string;
    borderColor?: string;
    width?: number | string;
    minWidth?: number | string;
    height?: number | string;
    minHeight?: number | string;
    borderRadius?: number;
    borderStroke?: number;
    boxShadow?: string;
    overflow?: 'visible' | 'hidden' | 'auto' | 'scroll' | 'clip';
    gap?: number;
    flexShrink?: number;
    opacity?: number;
    disabled?: TCoreLayoutOptions;
    hovered?: TCoreLayoutOptions;
    error?: TCoreLayoutOptions;
    pressed?: TCoreLayoutOptions;
} & TCoreOptions;
